import { useEffect, useState } from 'react';
import { api } from '../api';
import { hostLabel, useActiveHost, useStore } from '../store';
import { newHostId, suggestName, type Host } from '../hosts';
import { BrandMark } from './BrandMark';
import { UpdateBanner } from './UpdateBanner';

/**
 * Entrada do app: escolher o servidor e autenticar nele.
 *
 * O app nao tem "o" servidor — tem uma lista. Cada host guarda seu proprio
 * token; a senha nunca e gravada, so o nome de usuario para poupar digitacao.
 */
export function LoginScreen() {
  const authError = useStore((s) => s.authError);
  const setUser = useStore((s) => s.setUser);
  const hosts = useStore((s) => s.hosts);
  const activeHostId = useStore((s) => s.activeHostId);
  const switchHost = useStore((s) => s.switchHost);
  const saveHost = useStore((s) => s.saveHost);
  const deleteHost = useStore((s) => s.deleteHost);
  const authenticate = useStore((s) => s.authenticate);

  const host = useActiveHost();

  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('admin');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<Host | null>(null);

  // Pre-preenche com o ultimo usuario daquele host e limpa a senha ao trocar.
  useEffect(() => {
    setUsername(host?.username ?? 'admin');
    setPassword('');
    setError(null);
  }, [host?.id, host?.username]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!host) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.login(username, password);
      authenticate(host.id, res.token, res.expiresAt, username);
      setUser(res.user);
      setPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <div className="login-screen">
        <HostForm
          host={editing}
          isNew={!hosts.some((h) => h.id === editing.id)}
          onSave={(h) => {
            saveHost(h);
            switchHost(h.id);
            setEditing(null);
          }}
          onDelete={
            hosts.length > 1 && hosts.some((h) => h.id === editing.id)
              ? () => {
                  deleteHost(editing.id);
                  setEditing(null);
                }
              : undefined
          }
          onCancel={() => setEditing(null)}
        />
      </div>
    );
  }

  return (
    <div className="login-screen">
      {/* Tambem aqui, e nao so depois de entrar: a UI fica embutida no
          binario, entao um problema de interface ou de conexao poderia
          impedir o login -- e justamente ai a atualizacao precisa estar
          alcancavel. */}
      <div className="login-update">
        <UpdateBanner />
      </div>

      <form className="login-card" onSubmit={submit}>
        <div className="login-brand">
          <BrandMark size={20} />
          <h1>Code Manager</h1>
        </div>
        <p className="sub">Vários agentes de IA no mesmo projeto, cada um no seu painel.</p>

        <div className="field">
          <label htmlFor="host">Servidor</label>
          <div className="row">
            <select
              id="host"
              className="grow"
              value={activeHostId ?? ''}
              onChange={(e) => switchHost(e.target.value)}
            >
              {hosts.map((h) => (
                <option key={h.id} value={h.id}>
                  {hostLabel(h)}
                  {h.kind === 'local' ? ' (local)' : ''}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="ghost"
              onClick={() => host && setEditing(host)}
              disabled={!host}
              title="Editar este servidor"
            >
              ⚙
            </button>
            <button
              type="button"
              className="ghost"
              onClick={() =>
                setEditing({
                  id: newHostId(),
                  name: '',
                  url: '',
                  kind: 'remote',
                  token: null,
                  tokenExpiresAt: null,
                  username: null,
                })
              }
              title="Adicionar servidor"
            >
              +
            </button>
          </div>
          {host && (
            <p className="host-url" title={host.url || 'mesma origem desta página'}>
              {host.url || 'mesma origem desta página'}
            </p>
          )}
        </div>

        {(error || authError) && <div className="form-error">{error ?? authError}</div>}

        <div className="field">
          <label htmlFor="user">Usuário</label>
          <input
            id="user"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
          />
        </div>

        <div className="field">
          <label htmlFor="pass">Senha</label>
          <input
            id="pass"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            autoFocus
          />
        </div>

        <div className="row" style={{ marginTop: 18 }}>
          <button className="primary grow" type="submit" disabled={busy || !password || !host}>
            {busy ? 'entrando…' : 'Entrar'}
          </button>
        </div>

        <p className="host-note">A senha não é gravada. O acesso expira e pede login de novo.</p>
      </form>
    </div>
  );
}

interface FormProps {
  host: Host;
  isNew: boolean;
  onSave: (host: Host) => void;
  onDelete?: () => void;
  onCancel: () => void;
}

function HostForm({ host, isNew, onSave, onDelete, onCancel }: FormProps) {
  const [name, setName] = useState(host.name);
  const [url, setUrl] = useState(host.url);

  // Nome sugerido pela URL enquanto o usuario nao escrever um proprio.
  const effectiveName = name.trim() || suggestName(url);

  return (
    <form
      className="login-card"
      onSubmit={(e) => {
        e.preventDefault();
        onSave({ ...host, name: effectiveName, url: url.trim().replace(/\/+$/, '') });
      }}
    >
      <div className="login-brand">
        <BrandMark size={20} />
        <h1>{isNew ? 'Novo servidor' : 'Editar servidor'}</h1>
      </div>

      <div className="field">
        <label htmlFor="h-url">Endereço</label>
        <input
          id="h-url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://codemanager.suavps.com"
          autoFocus
        />
        <p className="host-note" style={{ marginTop: 6 }}>
          Endereço do daemon naquela máquina. Vazio = mesma origem desta página.
        </p>
      </div>

      <div className="field">
        <label htmlFor="h-name">Nome</label>
        <input
          id="h-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={suggestName(url)}
        />
      </div>

      <div className="row" style={{ marginTop: 16 }}>
        {onDelete && (
          <button type="button" className="ghost danger" onClick={onDelete}>
            excluir
          </button>
        )}
        <span className="grow" />
        <button type="button" onClick={onCancel}>
          Cancelar
        </button>
        <button className="primary" type="submit">
          Salvar
        </button>
      </div>
    </form>
  );
}
